import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getProjectBySlug } from '@/mocks/projects';
import { useProjectI18n } from '@/hooks/useProjectI18n';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';

export default function ProjectDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { t_title, t_tag, t_description, t_location, t_client } = useProjectI18n();
  const project = slug ? getProjectBySlug(slug) : undefined;
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);

  const isTR = i18n.language === 'tr' || i18n.language?.startsWith('tr');

  useEffect(() => {
    if (!project) {
      navigate('/projeler', { replace: true });
    }
  }, [project, navigate]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowLeft' && lightboxIndex > 0) setLightboxIndex(lightboxIndex - 1);
      if (e.key === 'ArrowRight' && lightboxIndex < (project?.gallery?.length ?? 0) - 1) setLightboxIndex(lightboxIndex + 1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, project]);

  if (!project) return null;

  const projectTitle = t_title(project) ?? '';
  const projectDesc = t_description(project) ?? '';
  const projectLocation = t_location(project) ?? '';
  const projectClient = t_client(project) ?? '';

  const seoTitle = isTR
    ? `${projectTitle} | OKAYISU LED Proje | ${projectLocation}`
    : `${projectTitle} | OKAYISU LED Project | ${projectLocation}`;

  const seoDescription = projectDesc.length > 160
    ? projectDesc.slice(0, 157) + '...'
    : projectDesc;

  const seoKeywords = isTR
    ? `${projectTitle}, LED aydınlatma projesi, ${project.category}, OKAYISU referans, ${projectLocation}`
    : `${projectTitle}, LED lighting project, ${project.category}, OKAYISU reference, ${projectLocation}`;

  const seoCanonical = isTR
    ? `https://www.okayisu.com/projeler/${project.slug}`
    : `https://www.okayisu.com/en/projeler/${project.slug}`;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: projectTitle,
    description: projectDesc,
    url: seoCanonical,
    image: project.image,
    about: {
      '@type': 'Place',
      name: projectLocation,
    },
    dateCreated: project.year,
    provider: {
      '@type': 'Organization',
      name: 'OKAYISU Aydınlatma',
    },
  };

  return (
    <div className="bg-white min-h-screen">
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={seoCanonical}
        ogImage={project.image}
        ogType="article"
        lang={isTR ? 'tr' : 'en'}
        structuredData={structuredData}
      />
      <Navbar />

      <div className="pt-20">
        {/* Hero Image */}
        <section className="relative w-full h-[360px] md:h-[520px] overflow-hidden">
          <img src={project.image} alt={projectTitle} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-gray-900/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 mb-4" aria-label="Breadcrumb">
              <button onClick={() => navigate(-1)} className="text-white/50 hover:text-amber-400 text-xs tracking-wide transition-colors duration-200 cursor-pointer" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {t('projectDetail.breadcrumb')}
              </button>
              <i className="ri-arrow-right-s-line text-white/30 text-xs" />
              <span className="text-amber-400 text-xs tracking-wide" style={{ fontFamily: "'Montserrat', sans-serif" }}>{projectTitle}</span>
            </nav>
            <span className="inline-block text-amber-400 text-xs tracking-[0.2em] uppercase font-medium bg-white/15 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t_tag(project)}</span>
            <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>{projectTitle}</h1>
            <div className="flex flex-wrap items-center gap-4 text-white/70 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
              <span className="flex items-center gap-1.5"><i className="ri-map-pin-line text-amber-400" />{projectLocation}</span>
              <span className="flex items-center gap-1.5"><i className="ri-calendar-line text-amber-400" />{project.year}</span>
              <span className="flex items-center gap-1.5"><i className="ri-building-line text-amber-400" />{projectClient}</span>
            </div>
          </div>
        </section>

        {/* Back Button - Top */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-6">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 bg-gray-900 hover:bg-amber-500 text-white px-5 py-2.5 rounded-full text-sm font-semibold tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg shadow-gray-900/20 hover:shadow-amber-500/30" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            <i className="ri-arrow-left-line" />
            {t('projectDetail.backToProjects')}
          </button>
        </div>

        {/* Info Section */}
        <section className="py-12 px-6 md:px-12 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 mb-5">
                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                <span className="text-amber-600 text-xs tracking-[0.2em] uppercase font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('projectDetail.aboutProject')}</span>
              </div>
              <p className="text-gray-700 text-sm md:text-base leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{projectDesc}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="text-amber-700 text-xs bg-amber-50 border border-amber-100 rounded-full px-3 py-1.5 whitespace-nowrap" style={{ fontFamily: "'Inter', sans-serif" }}>{project.category}</span>
                <span className="text-gray-600 text-xs bg-gray-100 border border-gray-200 rounded-full px-3 py-1.5 whitespace-nowrap" style={{ fontFamily: "'Inter', sans-serif" }}>{project.year}</span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <h3 className="text-gray-900 text-sm font-bold mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('projectDetail.projectSummary')}</h3>
              <div className="space-y-4">
                {[
                  { label: t('projectDetail.category'), value: project.category },
                  { label: t('projectDetail.year'), value: project.year },
                  { label: t('projectDetail.client'), value: projectClient },
                  { label: t('projectDetail.location'), value: projectLocation },
                ].map((row, i) => (
                  <div key={i}>
                    {i > 0 && <div className="w-full h-px bg-gray-200 mb-4" />}
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>{row.label}</span>
                      <span className="text-gray-800 text-xs font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>{row.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section className="py-12 px-6 md:px-12 max-w-7xl mx-auto border-t border-gray-100">
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
            <span className="text-amber-600 text-xs tracking-[0.2em] uppercase font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('projectDetail.gallery')}</span>
          </div>
          {project.gallery && project.gallery.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {project.gallery.map((item, idx) => {
                const isVideo = item.endsWith('.mp4') || item.endsWith('.webm') || item.endsWith('.mov');
                return (
                  <div key={idx} className="relative group overflow-hidden rounded-xl bg-gray-100 cursor-pointer" onClick={() => setLightboxIndex(idx)}>
                    {isVideo ? (
                      <video src={item} controls className="w-full h-48 md:h-64 object-cover" preload="metadata" />
                    ) : (
                      <>
                        <img src={item} alt={`${projectTitle} - ${idx + 1}`} className="w-full h-48 md:h-64 object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                          <i className="ri-zoom-in-line text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-2xl" />
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-2xl p-10 text-center border border-gray-100">
              <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                <i className="ri-image-line text-gray-400 text-xl" />
              </div>
              <p className="text-gray-500 text-sm mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>{t('projectDetail.galleryEmpty')}</p>
              <p className="text-gray-400 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>{t('projectDetail.galleryEmptyDesc')}</p>
            </div>
          )}
        </section>

        {/* Back Navigation */}
        <section className="py-10 px-6 md:px-12 max-w-7xl mx-auto border-t border-gray-100">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-gray-600 hover:text-amber-600 transition-colors duration-200 cursor-pointer">
              <i className="ri-arrow-left-line" />
              <span className="text-sm font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('projectDetail.backToList')}</span>
            </button>
            <button onClick={() => navigate('/#contact')} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-full text-sm font-semibold tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('projectDetail.contactForProject')}
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </section>
      </div>

      <Footer />

      {/* Sticky Floating Back Button */}
      <div className={`fixed left-6 bottom-6 z-50 transition-all duration-500 ${scrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 bg-gray-900 hover:bg-amber-500 text-white pl-4 pr-5 py-3 rounded-full text-sm font-semibold tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap shadow-xl shadow-gray-900/30 hover:shadow-amber-500/40" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          <i className="ri-arrow-left-line" />
          {t('projectDetail.backToProjects')}
        </button>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && project.gallery && project.gallery.length > 0 && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4 cursor-pointer" onClick={() => setLightboxIndex(null)}>
          <button className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors z-10" onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}>
            <i className="ri-close-line text-3xl" />
          </button>
          {lightboxIndex > 0 && (
            <button
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full text-white transition-colors z-10"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }}
            >
              <i className="ri-arrow-left-line text-xl" />
            </button>
          )}
          {lightboxIndex < project.gallery.length - 1 && (
            <button
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full text-white transition-colors z-10"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }}
            >
              <i className="ri-arrow-right-line text-xl" />
            </button>
          )}
          <div className="flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <img src={project.gallery[lightboxIndex]} alt={`${projectTitle} - ${lightboxIndex + 1}`} className="max-w-full max-h-[80vh] rounded-lg object-contain" />
            <div className="flex items-center gap-2 text-white/60 text-xs">
              <span>{lightboxIndex + 1} / {project.gallery.length}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}